import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TopNav from "../components/TopNav";
import { APIProposal, getAllProposals } from "../api/proposals";
import { APICustomer, getAllCustomers } from "../api/customers";
import { formatMoney } from "../utils";
import pageStyle from "./Analytics.module.css";

const DAY = 86400000;
const PERIODS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "12 months" },
  { value: 0, label: "All time" },
];
const STAGES = [
  { status: "LEAD", label: "Lead" },
  { status: "QUOTED", label: "Quoted" },
  { status: "WON", label: "Won" },
  { status: "DONE", label: "Done" },
];

const isWon = (p: APIProposal) => p.status === "WON" || p.status === "DONE";
// Revenue counts on the invoice date when there is one.
const saleDate = (p: APIProposal) => p.invoiceDate || p.createdAt || 0;
const compact = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : formatMoney(n);

function StatTile({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className={pageStyle.tile}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <div className={pageStyle.tileValue}>{value}</div>
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </div>
  );
}

// Single-series column chart: won revenue per month.
function MonthlyRevenue({ months }: { months: { label: string; total: number; count: number }[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(...months.map((m) => m.total), 1);
  // Round the top of the axis to a clean number.
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const ticks = [0, top / 2, top];

  return (
    <section className={pageStyle.card}>
      <div className={pageStyle.cardHeader}>
        <div>
          <Typography variant="subtitle1">Won revenue by month</Typography>
          <Typography variant="body2" color="text.secondary">
            Last 12 months, Won and Done proposals
          </Typography>
        </div>
        <Button size="small" onClick={() => setAsTable(!asTable)}>
          {asTable ? "Show chart" : "Show table"}
        </Button>
      </div>
      {asTable ? (
        <table className={pageStyle.table}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Jobs</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.label}>
                <td>{m.label}</td>
                <td>{m.count}</td>
                <td>{formatMoney(m.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={pageStyle.chart}>
          <div className={pageStyle.yAxis}>
            {[...ticks].reverse().map((t) => (
              <span key={t}>{compact(t)}</span>
            ))}
          </div>
          <div className={pageStyle.plot}>
            {ticks.map((t) => (
              <div
                key={t}
                className={pageStyle.gridline}
                style={{ bottom: `${(t / top) * 100}%` }}
              />
            ))}
            <div className={pageStyle.columns}>
              {months.map((m, i) => (
                <div
                  key={m.label}
                  className={pageStyle.slot}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => setHover(hover === i ? null : i)}
                  aria-label={`${m.label}: ${formatMoney(m.total)}, ${m.count} jobs`}
                >
                  <div
                    className={pageStyle.column}
                    style={{ height: `${(m.total / top) * 100}%` }}
                  />
                  {hover === i && (
                    <div className={pageStyle.tooltip}>
                      <strong>{m.label}</strong>
                      <div>{formatMoney(m.total)}</div>
                      <div>
                        {m.count} job{m.count === 1 ? "" : "s"}
                      </div>
                    </div>
                  )}
                  <span className={pageStyle.xLabel}>{m.label.split(" ")[0]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AnalyticsPage() {
  const [proposals, setProposals] = useState<APIProposal[]>([]);
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  const [period, setPeriod] = useState(90);

  useEffect(() => {
    getAllProposals().then(setProposals);
    getAllCustomers().then(setCustomers);
  }, []);

  const stats = useMemo(() => {
    const since = period ? Date.now() - period * DAY : 0;
    const inPeriod = proposals.filter((p) => (p.createdAt || 0) >= since);
    const wonInPeriod = proposals.filter((p) => isWon(p) && saleDate(p) >= since);
    const wonRevenue = wonInPeriod.reduce((s, p) => s + (p.totalAmount || 0), 0);
    const open = proposals.filter(
      (p) => (p.status === "LEAD" || p.status === "QUOTED") && !p.archivedAt
    );
    const closeRate = inPeriod.length
      ? inPeriod.filter(isWon).length / inPeriod.length
      : 0;
    const newCustomers = customers.filter(
      (c) => (c.createdAt || 0) >= since
    ).length;

    const stages = STAGES.map((s) => {
      const ps = inPeriod.filter((p) => p.status === s.status);
      return {
        ...s,
        count: ps.length,
        total: ps.reduce((sum, p) => sum + (p.totalAmount || 0), 0),
      };
    });

    const byCustomer = new Map<string, { name: string; total: number; jobs: number }>();
    for (const p of wonInPeriod) {
      const e = byCustomer.get(p.customerId) || {
        name: `${p.firstName} ${p.lastName}`.trim() || "Unnamed customer",
        total: 0,
        jobs: 0,
      };
      e.total += p.totalAmount || 0;
      e.jobs++;
      byCustomer.set(p.customerId, e);
    }
    const topCustomers = Array.from(byCustomer.entries())
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 8);

    return {
      wonRevenue,
      wonCount: wonInPeriod.length,
      avgDeal: wonInPeriod.length ? wonRevenue / wonInPeriod.length : 0,
      pipeline: open.reduce((s, p) => s + (p.totalAmount || 0), 0),
      openCount: open.length,
      closeRate,
      proposalsCount: inPeriod.length,
      newCustomers,
      stages,
      topCustomers,
    };
  }, [proposals, customers, period]);

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const won = proposals.filter(
        (p) => isWon(p) && saleDate(p) >= d.getTime() && saleDate(p) < next.getTime()
      );
      return {
        label: d.toLocaleDateString([], { month: "short", year: "numeric" }),
        total: won.reduce((s, p) => s + (p.totalAmount || 0), 0),
        count: won.length,
      };
    });
  }, [proposals]);

  const maxStage = Math.max(...stats.stages.map((s) => s.total), 1);

  return (
    <div className="page">
      <TopNav pageTitle="Analytics" />
      <main className={pageStyle.main}>
        <div className={pageStyle.content}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            onChange={(_, v) => v !== null && setPeriod(v)}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <div className={pageStyle.tiles}>
            <StatTile
              label="Won revenue"
              value={formatMoney(stats.wonRevenue)}
              note={`${stats.wonCount} job${stats.wonCount === 1 ? "" : "s"}`}
            />
            <StatTile
              label="Close rate"
              value={`${Math.round(stats.closeRate * 100)}%`}
              note={`of ${stats.proposalsCount} proposals`}
            />
            <StatTile label="Average won job" value={formatMoney(stats.avgDeal)} />
            <StatTile
              label="Open pipeline"
              value={formatMoney(stats.pipeline)}
              note={`${stats.openCount} lead or quoted, all time`}
            />
            <StatTile label="New customers" value={String(stats.newCustomers)} />
            <StatTile label="Proposals written" value={String(stats.proposalsCount)} />
          </div>

          <MonthlyRevenue months={months} />

          <div className={pageStyle.twoCol}>
            <section className={pageStyle.card}>
              <Typography variant="subtitle1">Pipeline by stage</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Proposals created in this period
              </Typography>
              {stats.stages.map((s) => (
                <Link
                  key={s.status}
                  to="/proposals"
                  className={pageStyle.barRow}
                  aria-label={`${s.label}: ${s.count} proposals, ${formatMoney(s.total)}`}
                >
                  <span className={pageStyle.barLabel}>
                    {s.label}
                    <span className={pageStyle.muted}> · {s.count}</span>
                  </span>
                  <span className={pageStyle.barTrack}>
                    <span
                      className={pageStyle.bar}
                      style={{ width: `${(s.total / maxStage) * 100}%` }}
                    />
                  </span>
                  <span className={pageStyle.barValue}>{formatMoney(s.total)}</span>
                </Link>
              ))}
            </section>

            <section className={pageStyle.card}>
              <Typography variant="subtitle1">Top customers</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                By won revenue in this period
              </Typography>
              {stats.topCustomers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No won jobs in this period.
                </Typography>
              ) : (
                <table className={pageStyle.table}>
                  <tbody>
                    {stats.topCustomers.map(([id, c]) => (
                      <tr key={id}>
                        <td>
                          <Link to={`/customers?id=${id}`}>{c.name}</Link>
                        </td>
                        <td className={pageStyle.muted}>
                          {c.jobs} job{c.jobs === 1 ? "" : "s"}
                        </td>
                        <td>{formatMoney(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AnalyticsPage;
